import { useEffect, useState } from "react";
import type { Participant } from "livekit-client";
import { ParticipantEvent } from "livekit-client";
import { PARTICIPANT_ROLES } from "@/react/helpers/liveStreamRoomHelpers";
import type { ProviderRoleChangeNotification } from "../types";
import { convertParticipantToPeer } from "./helpers";
import { useLivekitLocalParticipant } from "./useLivekitLocalParticipant";

export const useLivekitRoleChangeNotification =
  (): ProviderRoleChangeNotification => {
    const localParticipant = useLivekitLocalParticipant();
    const [hasRoleChangedToModerator, setHasRoleChangedToModerator] =
      useState(false);
    const hideModeratorNotification = () => setHasRoleChangedToModerator(false);

    useEffect(() => {
      const onRoleChanged = (
        prevMetadata?: string,
        participant?: Participant,
      ) => {
        const targetParticipant = participant || localParticipant;
        const prevPeer = convertParticipantToPeer({
          ...participant,
          metadata: prevMetadata || targetParticipant.metadata,
        } as Participant);
        const peer = convertParticipantToPeer(targetParticipant);
        if (
          peer.isLocal &&
          peer.roleName === PARTICIPANT_ROLES.MODERATOR &&
          prevPeer.roleName !== PARTICIPANT_ROLES.MODERATOR
        ) {
          setHasRoleChangedToModerator(true);
        }
      };

      localParticipant.on(
        ParticipantEvent.ParticipantMetadataChanged,
        onRoleChanged,
      );

      return () => {
        localParticipant.off(
          ParticipantEvent.ParticipantMetadataChanged,
          onRoleChanged,
        );
      };
    }, [localParticipant]);

    return {
      roleChangedToModerator: hasRoleChangedToModerator,
      hideModeratorNotification,
    };
  };
